import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { type LocationState } from "history";

import { Button } from "../../components/Button";
import { LoadingButton } from "../../components/LoadingButton";
import { WorkplaceModalFloatingFooter } from "../Modal/FloatingFooter";
import { ViewStaffJobsButton } from "../OpenShifts/ViewStaffJobsButton";

interface WorkplaceProfileModalFooterProps {
  workplaceId: string;
  isLoading: boolean;
  locationState: LocationState;
  source: "flex-workplace-profile-modal" | "hiring-workplace-profile-modal";
  /**
   * Omit to hide the primary open-shifts CTA entirely (e.g. Home Healthcare/Hospice
   * workplaces with no open shifts).
   */
  openShifts?: {
    count: number;
    href: string;
    analyticsTrigger: string;
  };
  /**
   * Omit to hide the secondary staff-jobs CTA (feature disabled or no placements).
   */
  staffJobs?: {
    placementsCount: number;
    placementCandidateId: string | undefined;
  };
}

export function WorkplaceProfileModalFooter(props: WorkplaceProfileModalFooterProps) {
  const { workplaceId, isLoading, locationState, source, openShifts, staffJobs } = props;

  return (
    <WorkplaceModalFloatingFooter>
      <Stack spacing={3} sx={{ width: "100%" }}>
        {isLoading ? (
          <LoadingButton fullWidth isLoading variant="contained">
            Loading
          </LoadingButton>
        ) : (
          <>
            {openShifts && (
              <OpenShiftsButton
                workplaceId={workplaceId}
                locationState={locationState}
                {...openShifts}
              />
            )}
            {staffJobs && (
              <ViewStaffJobsButton
                workplaceId={workplaceId}
                source={source}
                placementsCount={staffJobs.placementsCount}
                placementCandidateId={staffJobs.placementCandidateId}
                locationState={locationState}
              />
            )}
          </>
        )}
      </Stack>
    </WorkplaceModalFloatingFooter>
  );
}

interface OpenShiftsButtonProps {
  workplaceId: string;
  count: number;
  href: string;
  analyticsTrigger: string;
  locationState: LocationState;
}

function OpenShiftsButton(props: OpenShiftsButtonProps) {
  const { workplaceId, count, href, analyticsTrigger, locationState } = props;
  const hasOpenShifts = count > 0;

  return (
    <Button
      fullWidth
      variant="contained"
      invert={!hasOpenShifts}
      disabled={!hasOpenShifts}
      href={href}
      locationState={locationState}
      onClick={() => {
        logEvent(APP_V2_USER_EVENTS.WORKPLACE_OPEN_SHIFTS_OPENED, {
          workplaceId,
          trigger: analyticsTrigger,
        });
      }}
    >
      {hasOpenShifts ? `View ${count} open shifts` : "No open shifts right now"}
    </Button>
  );
}
